import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Card } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { i18nService } from '../../i18n/i18nService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary 捕获到错误:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 'var(--spacing-lg)',
          background: 'var(--color-bg-primary)'
        }}>
          <Card
            title={i18nService.t('common.errorBoundaryTitle')}
            style={{ maxWidth: '600px', width: '100%' }}
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
                type="primary"
              >
                {i18nService.t('common.retry')}
              </Button>
            }
          >
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <p style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-sm)' }}>
                {this.state.error?.message || i18nService.t('common.unknownError')}
              </p>
              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details style={{ marginTop: 'var(--spacing-md)' }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                    {i18nService.t('common.errorDetails')}
                  </summary>
                  <pre style={{
                    marginTop: 'var(--spacing-sm)',
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--border-radius-md)',
                    overflow: 'auto',
                    fontSize: '12px',
                    color: 'var(--color-text-primary)'
                  }}>
                    {this.state.error?.stack}
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
            <Button
              type="default"
              onClick={() => window.location.reload()}
              style={{ width: '100%' }}
            >
              {i18nService.t('common.refreshPage')}
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

